/* eslint-disable consistent-return */
import * as Yup from "yup";
import React, { useCallback, useRef, useState } from "react";
import { FiAtSign } from "react-icons/fi";
import { Formik } from "formik";
import { IoMdArrowBack, IoMdArrowForward } from "react-icons/io";
import { MdCheck } from "react-icons/md";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";

import ContainedButton from "components/layout/form/containedButton";
import MailingAddress from "components/MI/adicionar-unidade/MailingAddress";
import OutlinedButton from "components/layout/form/outlinedButton";
import TextField from "components/layout/form/textField";
import api from "services/api";
import theme from "styles/theme";

import {
  setUnitsList,
  toggleSelectedUnit,
} from "store/actions/select-unit-actions";

import {
  AutomaticDebtSection,
  ButtonsContainer,
  Container,
  DaySection,
  EmailSection,
  Error,
  FormContainer,
  HasEmailSection,
  Header,
  InputsContainer,
  WarningSection,
} from "./styles";
import { useAdicionarUnidade } from "features/adicionar-unidade/hooks/useAdicionarUnidade";
// eslint-disable-next-line
import TermoAdesao from "components/Portal/TermoAdesao";

type BillingFormValues = {
  dueDay: string;
  deliveryType: string;
  email?: string;
  emailConfirm?: string;
};

const BillingData = (): JSX.Element => {
  const submitFormRef = useRef<(() => void) | null>(null);
  const [email] = useState(() => {
    const letEmail = localStorage.getItem("@UltragazEmpresarial:loginEmail");
    if (letEmail === "null" || letEmail === "undefined") {
      return null;
    }
    return letEmail;
  });
  const userIdFromStorage = localStorage.getItem("@UltragazEmpresarial:userId");
  const userDocument = localStorage.getItem("@UltragazEmpresarial:document");
  const [openSwitchAddressModal, setOpenSwitchAddressModal] = useState();
  const [showTerm, setShowTerm] = useState(false);

  const [loading, setLoading] = useState(false);
  const addNewUnitReducer = useSelector(
    // @ts-ignore
    (state) => state.addNewUnitReducer
  );
  const {
    dueDay,
    deliveryType,
    address,
    addressShipLetterTo: addressShipLetterToRedux,
  } = addNewUnitReducer;
  const [addressShipLetterTo, setAddressShipLetterTo] = useState(
    addressShipLetterToRedux?.address ? addressShipLetterToRedux : address
  );
  const dispatch = useDispatch();

  const { newUnitData, setNewUnitData, setStep } = useAdicionarUnidade();

  const validatorSchema = (): Yup.ObjectSchema<BillingFormValues> =>
    Yup.object().shape({
      dueDay: Yup.string().required(
        "É necessário selecionar um dia de vencimento"
      ),
      deliveryType: Yup.string().required(
        "É necessário selecionarum tipo de fatura"
      ),
      email: Yup.string().when("deliveryType", {
        is: (val) => val === "EMAIL",
        then: Yup.string()
          .email("E-mail inválido")
          .required("É necessário digitar um email"),
        otherwise: Yup.string(),
      }),
      emailConfirm: Yup.string().when("deliveryType", {
        is: (val) => val === "EMAIL",
        then: Yup.string()
          .email("E-mail inválido")
          .oneOf([Yup.ref("email")], "Os e-mails não combinam")
          .required("É necessário confirmar o e-mail"),
        otherwise: Yup.string(),
      }),
    });

  const requestPostNewUnit = useCallback(
    async ({ dueDay, deliveryType }) => {
      const headers = {
        access_token: localStorage.getItem("@UltragazEmpresarial:accessToken"),
      };

      const {
        externalCondominiumId,
        externalBlockId,
        externalUnitId,
        unitInstanceId,
        name,
        hasGasMeter,
        hasGasReleased,
        address,
        condominiumName,
      } = newUnitData;

      const body: Record<string, unknown> = {
        condominiumName,
        externalCondominiumId,
        externalBlockId,
        externalUnitId,
        unitInstanceId,
        name,
        hasGasMeter,
        hasGasReleased,
        address,
        dueDay,
        deliveryType,
        addressShipLetterTo: address,
      };

      const result = await api.post(
        `industrial/customer/v1/customers/me/units`,
        body,
        {
          headers,
        }
      );

      setLoading(false);

      return result;
    },
    [api, addressShipLetterTo, newUnitData]
  );

  const requestPatchCustomers = useCallback(
    async (email) => {
      const headers = {
        access_token: localStorage.getItem("@UltragazEmpresarial:accessToken"),
        EMAIL_CONFIRMATION_URL: `${process.env.REACT_APP_URL}/verificar-email`,
      };
      const body = {
        email,
      };
      try {
        await api.patch(`industrial/customer/v1/customers/me`, body, {
          headers,
        });
      } catch (error) {
        toast.error(
          "Não foi possível cadastar o E-mail, tente novamente em Meu Perfil"
        );
      }
    },
    [api, setLoading]
  );

  const requestGetUnits = useCallback(async () => {
    setLoading(true);
    const accessToken = localStorage.getItem(
      "@UltragazEmpresarial:accessToken"
    );
    const headers = { access_token: accessToken };
    const response = await api.get(
      `industrial/customer/v1/customers/me/units`,
      { headers }
    );
    setLoading(false);
    const { unitInstanceId } = newUnitData;
    const { data } = response;
    if (data && data.length > 0) {
      dispatch(setUnitsList(data));
      const unitFilted = data.find(
        (unit) => unitInstanceId === unit.instanceId
      );
      if (unitFilted) {
        dispatch(toggleSelectedUnit(unitFilted));
      }
    }
  }, [api, dispatch, toggleSelectedUnit, setLoading, newUnitData]);

  const handleSubmit = useCallback(
    async (values: BillingFormValues): Promise<void> => {
      setNewUnitData({
        ...newUnitData,
        dueDay: values.dueDay,
        deliveryType: values.deliveryType,
        addressShipLetterTo,
      });

      if (!loading) {
        setLoading(true);
        try {
          await requestPostNewUnit(values);
          setLoading(false);
          if (
            values.deliveryType === "EMAIL" &&
            values.email !==
              localStorage.getItem("@UltragazEmpresarial:loginEmail")
          ) {
            requestPatchCustomers(values.email);
          }
          await requestGetUnits();

          setStep(4);
        } catch (err) {
          setLoading(false);
          const erro = err?.response?.data;
          if (erro) {
            if (erro?.code === "422.031") {
              toast.error("Unidade já vinculada ao perfil");
              return;
            }
            if (err?.response?.status !== 401) {
              toast.error(err?.response?.data?.message);
            }
          } else if (err?.response?.status !== 401) {
            toast.error("Ocorreu um erro interno na aplicação");
          }
        }
      }
    },
    [addressShipLetterTo]
  );

  const handleReturnLastPage = useCallback(
    ({ dueDay, deliveryType }) => {
      setShowTerm(false);
      setNewUnitData({
        ...newUnitData,
        dueDay,
        deliveryType,
        addressShipLetterTo,
      });
      setStep(2);
    },
    [setStep, addressShipLetterTo, setNewUnitData, newUnitData]
  );

  const newUnitDataInstanceId = newUnitData?.unitInstanceId;

  return (
    <Container>
      <Header>
        <h1>Dados de cobrança</h1>

        <span>
          As alterações solicitadas serão efetuadas em até 01 (um) mês
        </span>
      </Header>

      <Formik
        initialValues={{
          dueDay: dueDay || "",
          deliveryType: deliveryType || "EMAIL",
          email: email || "",
          emailConfirm: email || "",
        }}
        onSubmit={handleSubmit}
        validationSchema={validatorSchema}
      >
        {({
          handleChange,
          handleBlur,
          values,
          errors,
          touched,
          validateForm,
          setTouched,
          submitForm,
        }) => (
          <FormContainer>
            {(() => {
              submitFormRef.current = submitForm;
              return null;
            })()}
            <InputsContainer>
              <DaySection>
                <h1>Selecione o melhor dia para vencimento</h1>
                <div>
                  <input
                    id="day1"
                    type="radio"
                    name="dueDay"
                    value="1"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    defaultChecked={values.dueDay === "1"}
                  />
                  <label htmlFor="day1">
                    01{" "}
                    <div>
                      <MdCheck size={10} />
                    </div>{" "}
                  </label>

                  <input
                    id="day5"
                    type="radio"
                    name="dueDay"
                    value="5"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    defaultChecked={values.dueDay === "5"}
                  />
                  <label htmlFor="day5">
                    05{" "}
                    <div>
                      <MdCheck size={10} />
                    </div>
                  </label>

                  <input
                    id="day10"
                    type="radio"
                    name="dueDay"
                    value="10"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    defaultChecked={values.dueDay === "10"}
                  />
                  <label htmlFor="day10">
                    10{" "}
                    <div>
                      <MdCheck size={10} />
                    </div>
                  </label>

                  <input
                    id="day15"
                    type="radio"
                    name="dueDay"
                    value="15"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    defaultChecked={values.dueDay === "15"}
                  />
                  <label htmlFor="day15">
                    15{" "}
                    <div>
                      <MdCheck size={10} />
                    </div>
                  </label>

                  <input
                    id="day20"
                    type="radio"
                    name="dueDay"
                    value="20"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    defaultChecked={values.dueDay === "20"}
                  />
                  <label htmlFor="day20">
                    20{" "}
                    <div>
                      <MdCheck size={10} />
                    </div>
                  </label>

                  <input
                    id="day25"
                    type="radio"
                    name="dueDay"
                    value="25"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    defaultChecked={values.dueDay === "25"}
                  />
                  <label htmlFor="day25">
                    25{" "}
                    <div>
                      <MdCheck size={10} />
                    </div>
                  </label>
                </div>
                {errors.dueDay && touched.dueDay && (
                  <Error>{`${errors.dueDay}`}</Error>
                )}
              </DaySection>

              {values.deliveryType === "EMAIL" && email && (
                <HasEmailSection>
                  <p>Você já cadastrou um e-mail para receber suas faturas</p>

                  <p>
                    <b>E-mail cadastrado:</b> {email}
                  </p>
                </HasEmailSection>
              )}

              {values.deliveryType === "EMAIL" && !email && (
                <>
                  <AutomaticDebtSection>
                    <p>
                      O e-mail será validado através do link enviado. As
                      alterações solicitadas serão efetuadas em até 01 (um) mês.
                      O e-mail informado também irá alterar o e-mail de Dados
                      Cadastrais.
                    </p>
                  </AutomaticDebtSection>
                  <EmailSection>
                    <div>
                      <TextField
                        propsLeadingIcon={{
                          size: 24,
                          color: "#c1c1c1",
                        }}
                        LeadingIcon={FiAtSign}
                        type="email"
                        name="email"
                        label="Digite seu e-mail"
                        value={values.email}
                        // @ts-ignore
                        errorCondition={errors.email && touched.email}
                        // @ts-ignore
                        errorMessage={errors.email}
                        onBlur={handleBlur}
                        onChange={handleChange}
                      />
                    </div>

                    <div>
                      <TextField
                        propsLeadingIcon={{
                          size: 24,
                          color: "#c1c1c1",
                        }}
                        LeadingIcon={FiAtSign}
                        type="email"
                        name="emailConfirm"
                        label="Repita seu e-mail"
                        value={values.emailConfirm}
                        // @ts-ignore
                        errorCondition={
                          errors.emailConfirm && !!touched.emailConfirm
                        }
                        // @ts-ignore
                        errorMessage={errors.emailConfirm}
                        onBlur={handleBlur}
                        onChange={handleChange}
                      />
                    </div>
                  </EmailSection>
                </>
              )}

              <WarningSection>
                <span>
                  Enviaremos seus boletos para o e-mail cadastrado acima.
                </span>
                <p>
                  Fique tranquilo, se preferir receber seu boleto pelo correio,
                  a alteração poderá ser feita na tela de dados cadastrais da
                  sua unidade.
                </p>
              </WarningSection>
            </InputsContainer>

            <ButtonsContainer>
              <ContainedButton
                type="button"
                continue
                id="Avançar"
                width="100%"
                propsTrailingIcon={{
                  size: 20,
                }}
                TrailingIcon={IoMdArrowForward}
                loading={loading || showTerm}
                onClick={async () => {
                  const errorsFound = await validateForm();
                  if (!errorsFound || Object.keys(errorsFound).length === 0) {
                    setShowTerm(true);
                  } else {
                    setTouched({
                      dueDay: true,
                      deliveryType: true,
                      email: true,
                      emailConfirm: true,
                    });
                  }
                }}
              >
                Ler os termos e continuar
              </ContainedButton>
              <OutlinedButton
                width="100%"
                propsLeadingIcon={{
                  size: 20,
                }}
                LeadingIcon={IoMdArrowBack}
                onClick={() => handleReturnLastPage(values)}
                color={theme.colors.purple}
              >
                Voltar
              </OutlinedButton>
            </ButtonsContainer>
            {showTerm && (
              <TermoAdesao
                show={showTerm}
                clientUserId={
                  userIdFromStorage && userDocument
                    ? `${userIdFromStorage}_${userDocument}_${newUnitDataInstanceId}`
                    : `${userIdFromStorage || ""}_${newUnitDataInstanceId}`
                }
                unityId={newUnitDataInstanceId}
                handleAccepted={() => {
                  setShowTerm(false);
                  toast.success("Termo de adesão aceito com sucesso!");
                  setTimeout(() => {
                    submitForm();
                  }, 0);
                }}
              />
            )}
          </FormContainer>
        )}
      </Formik>
      <MailingAddress
        setAddressShipLetterTo={setAddressShipLetterTo}
        openModal={openSwitchAddressModal}
        modalFunction={setOpenSwitchAddressModal}
      />
    </Container>
  );
};

export default BillingData;
